import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { UpgradeSubscriptionButton } from "components/action-buttons/action-buttons";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { useState } from "react";
import { ClipConfigProps, ClipStepsControlProps } from "../../clip-menu";
import { ClipConfiguration } from "../../models";
import { HistoricClips } from "./historic-clips";
import { RecentClips } from "./recent-clips";

export const MAX_CLIPS = 5;

export const NewClip = (props: ClipConfigProps & ClipStepsControlProps) => {
  const [showMore, setShowMore] = useState(false);

  const createNewClipContent = () => {
    return (
      <>
        <Typography variant="h5">New</Typography>
        <TextField
          fullWidth
          id="clip-name"
          inputProps={{ maxLength: 200 }}
          label="Name"
          value={props.clipConfig.name}
          onChange={(event) =>
            props.setClipConfig((prev: ClipConfiguration) => ({
              ...prev,
              name: event.target.value,
            }))
          }
          onKeyDown={(ev) => {
            if (ev.key === "Enter" && !!props.clipConfig.name) {
              props.draw.deleteAll();
              props.setActiveStep(
                Math.min(props.activeStep + 1, props.maxSteps)
              );
              ev.preventDefault();
            }
          }}
          variant="outlined"
          size="small"
        />
        <Button
          sx={{ alignSelf: "flex-end" }}
          variant="text"
          disabled={!props.clipConfig.name}
          onClick={() => {
            props.draw.deleteAll();
            props.setActiveStep(Math.min(props.activeStep + 1, props.maxSteps));
          }}
        >
          Create
        </Button>
      </>
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={1}
      height="calc(86vh - 64px)"
    >
      <PermissionsWrapper
        keys={[PermissionsKeys.clipCreate]}
        messages={{
          // ROLE_DENY just needs a message when they got 0 clips to make the empty space
          // look less busted. If they got some clips to list we dont need to show anything here
          [PermissionsReasons.ROLE_DENY]: props.clips.length === 0 && ( 
            <Alert variant="outlined" severity="info">
              <AlertTitle>Access limited</AlertTitle>
              Your role does have access to create new clips
            </Alert>
          ),
          [PermissionsReasons.UPGRADE_REQUIRED]: (
            <Stack direction="column" spacing={2}>
              <Alert variant="outlined" severity="info">
                <AlertTitle>
                  {props.clips.length === 0
                    ? "Unlock Clip"
                    : "Subscription Required"}
                </AlertTitle>
                {props.clips.length === 0
                  ? "To use Clip and access all its features, you will need a subscription."
                  : "Get back to where you left off, subscribe to Team or Pro to access historic clips and start making new clips."}
              </Alert>
              <Box alignSelf="flex-end" marginTop="auto" display="flex">
                <UpgradeSubscriptionButton
                  variant="contained"
                  text={props.clips.length === 0 ? "start trial" : "subscribe"}
                  size="small"
                />
              </Box>
            </Stack>
          ),
        }}
      >
        {!showMore && createNewClipContent()}
      </PermissionsWrapper>

      {/* Has clips history */}
      {props.clips.length > 0 && !showMore && (
        <>
          <Typography variant="h5">Recent</Typography>

          <RecentClips
            onSelect={(clip: ClipConfiguration) => {
              props.setActiveStep(clip.activeStep);
              props.setClipConfig(clip);
            }}
            mapRef={props.mapRef}
            maxClips={MAX_CLIPS}
            clips={props.clips}
            draw={props.draw}
          />

          {!!props.clips.length && props.clips.length > MAX_CLIPS && (
            <Box alignSelf="flex-end" marginTop="auto" display="flex">
              <Button onClick={() => setShowMore(true)}>More</Button>
            </Box>
          )}
        </>
      )}
      {showMore && (
        <HistoricClips {...props} onExit={() => setShowMore(false)} />
      )}
    </Box>
  );
};
